/**
 * Beta Access matrix — a platform-admin-only grid of the account's users
 * against the registered Beta features, one toggle per (user, feature) cell.
 * Replaces the inline per-row control so the surface scales as more Beta
 * features are registered. Reuses the existing listAccountUsers / listBetaFeatures
 * queries and the setUserBetaFeatureAccess mutation; the server is the gate.
 */
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";

/**
 * Platform-admin Beta Access matrix: rows are account users, columns are the
 * registered Beta features, each cell grants or revokes one feature for one
 * user. Platform-admin rows show every feature as granted and non-editable.
 * @returns the Beta Access tab content
 */
export function BetaAccessTab() {
	const usersQuery = trpc.permissionGroups.listAccountUsers.useQuery({ limit: 50, offset: 0 });
	const featuresQuery = trpc.permissionGroups.listBetaFeatures.useQuery();

	// Server-state-driven, so a failed write simply leaves the cell as it was.
	const setBetaAccessMutation = trpc.permissionGroups.setUserBetaFeatureAccess.useMutation({
		onSuccess: () => {
			void usersQuery.refetch();
		},
	});

	const users = usersQuery.data?.users ?? [];
	const features = featuresQuery.data ?? [];

	return (
		<div className="space-y-4">
			<p className="text-muted-foreground text-sm">
				Grant or revoke access to Beta features per user. Platform admins always have access to
				every Beta feature.
			</p>
			<Card>
				<CardContent className="p-0">
					{features.length === 0 ? (
						<div className="text-muted-foreground p-6 text-sm">
							No Beta features are registered.
						</div>
					) : users.length === 0 ? (
						<div className="text-muted-foreground p-6 text-sm">
							No users in this account yet.
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>User</TableHead>
									{features.map((feature) => (
										<TableHead key={feature.key} className="text-center">
											{feature.name}
										</TableHead>
									))}
								</TableRow>
							</TableHeader>
							<TableBody>
								{users.map((user) => {
									const isSuperAdmin = user.role === "super_admin";
									return (
										<TableRow key={user.id}>
											<TableCell className="font-medium">{user.email}</TableCell>
											{features.map((feature) => {
												const granted = isSuperAdmin || user.betaFeatures.includes(feature.key);
												return (
													<TableCell key={feature.key} className="text-center">
														<Checkbox
															checked={granted}
															disabled={isSuperAdmin || setBetaAccessMutation.isPending}
															title={
																isSuperAdmin
																	? "Platform admins always see Beta features"
																	: feature.description
															}
															aria-label={`Toggle ${feature.name} Beta access for ${user.email}`}
															onCheckedChange={(checked) => {
																setBetaAccessMutation.mutate({
																	userId: user.id,
																	featureKey: feature.key,
																	granted: checked === true,
																});
															}}
														/>
													</TableCell>
												);
											})}
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
